import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ShareModal from './ShareModal';

const Item = ({ item, index, moveItem }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'item',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1, padding: '8px', border: '1px solid var(--border-color)', margin: '4px', cursor: 'move' }}
    >
      {item.name}
    </div>
  );
};

const TopicDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [userRanking, setUserRanking] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [totalVoters, setTotalVoters] = useState(0);
  const [message, setMessage] = useState('');
  const [isVoting, setIsVoting] = useState(false);
  const [items, setItems] = useState([]);

// Construct API URL
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        // Fetch topic data
        const topicRes = await axios.get(`${apiUrl}/api/topics/${id}`);
        setTopic(topicRes.data);

        // Only fetch voters for system topics
        if (topicRes.data.isSystem) {
          const votersRes = await axios.get(`${apiUrl}/api/topics/${id}/voters`);
          setTotalVoters(votersRes.data.totalVoters);
        } else {
          setTotalVoters(0); // No voters for personal topics
        }

        if (token) {
          setIsSignedIn(true);
          const [userRes, rankingRes] = await Promise.all([
            axios.get(`${apiUrl}/api/auth/verify`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${apiUrl}/api/topics/${id}/ranking`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          setIsAdmin(userRes.data.user.role === 'admin');
          setUserRanking(rankingRes.data);
          setItems(rankingRes.data.items?.length ? rankingRes.data.items : topicRes.data.items);
        } else {
          setItems(topicRes.data.items);
        }
      } catch (error) {
        //console.error('Error fetching data:', error);
        setMessage('Failed to fetch data: ' + (error.response?.data?.error || 'Unknown error'));
      }
    };
    fetchData();
  }, [id]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setMessage('Link copied to clipboard!');
    setTimeout(() => setMessage(''), 3001);
  };

  const handleVote = () => {
    setIsVoting(true);
  };

  const handleCancel = () => {
    setIsVoting(false);
    setItems(userRanking?.items?.length ? userRanking.items : topic.items);
  };

  const handlePublish = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${apiUrl}/api/topics/${id}/rank`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserRanking(response.data.ranking);
      setTopic(response.data.topic);
      setIsVoting(false);
      setMessage('Ranking published successfully!');
      if (topic.isSystem) {
        const votersRes = await axios.get(`${apiUrl}/api/topics/${id}/voters`);
        setTotalVoters(votersRes.data.totalVoters);
      }
    } catch (error) {
      //console.error('Error publishing ranking:', error);
      setMessage('Failed to publish ranking: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteRanking = async () => {
    if (!window.confirm('This will remove your votes for this poll, and your selections will no longer be reflected in this Official MyTop25 Poll. Still OK to Delete?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/api/topics/${id}/rank`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserRanking(null);
      setItems(topic.items);
      const topicRes = await axios.get(`${apiUrl}/api/topics/${id}`);
      setTopic(topicRes.data);
      if (topic.isSystem) {
        const votersRes = await axios.get(`${apiUrl}/api/topics/${id}/voters`);
        setTotalVoters(votersRes.data.totalVoters);
      }
      setMessage('Ranking deleted successfully!');
    } catch (error) {
      //console.error('Error deleting ranking:', error);
      setMessage('Failed to delete ranking: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    setItems(updatedItems);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    }) + ' ET';
  };

  const isOutdated = userRanking?.publishDate && topic?.publishDate && new Date(userRanking.publishDate) < new Date(topic.publishDate);

  if (!topic) {
    return <div>{message || 'Loading...'}</div>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <ShareModal />
      <div>
        <h1>{topic.name}</h1>
        <h2>{topic.description || 'No description'}</h2>
        {/*<button onClick={handleShare}>Share Link</button>*/}
        
        {message && <p>{message}</p>}

        {/* {topic.isSystem && topic.items.length > 25 && (
          <div>
            <h3>Receiving votes: {topic.items.slice(25, 28).map(item => item.name).join(', ')}</h3>
          </div>
        )} */}

        {topic.isSystem && (
          <div>
            <h3>MyTop25 Published Date: {formatDate(topic.publishDate)}</h3>
          </div>
        )}

        {isAdmin && topic.isSystem && ( // Removed "isAdmin && "so this shows to regular users
          <div>
            <h3>Total users who have voted on this poll: {totalVoters}</h3>
          </div>
        )}

        {isSignedIn && topic.isSystem && userRanking?.publishDate && (
          <div>
            <h3 className={isOutdated ? 'warning-text' : ''}>
              My Published Date: {formatDate(userRanking.publishDate)}
            </h3>
            {isOutdated && (
              <p className="warning-text">MyTop25 has Re-Published this Poll, time to vote again!</p>
            )}
          </div>
        )}

        {/* <h3>Items:</h3> */}
        <table className="topic-items-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Name</th>
              {topic.isSystem && <th>Points</th>}
            </tr>
          </thead>
          <tbody>
            {topic.items.slice(0, 25).map((item, index) => (
              <tr key={index}>
                <td>{index + 1}.</td>
                <td>{item.name}</td>
                {topic.isSystem && <td>{item.points || 0}</td>}
              </tr>
            ))}
          </tbody>
        </table>

        {topic.isSystem && topic.items.length > 25 && (
          <div>
            <h3>Receiving votes: {topic.items.slice(25, 28).map(item => item.name).join(', ')}</h3>
          </div>
        )}

        {!topic.isSystem && topic.items.length > 25 && (
          <div>
            <h3>Receiving votes: {topic.items.slice(25, 28).map(item => item.name).join(', ')}</h3>
          </div>
        )}

        {topic.isSystem && topic.final ? (
          <p>This is the final poll, this poll is closed.</p>
        ) : (
          <>
            {!isOutdated && isSignedIn && topic.isSystem && !isVoting && (
              <>
                {!userRanking?.publishDate ? (
                  <button onClick={handleVote}>Vote Now!</button>
                ) : (
                  <>
                    <button onClick={handleVote}>Edit my Votes</button>
                    <button onClick={handleDeleteRanking}>Delete My Votes</button>
                  </>
                )}
              </>
            )}

            {isOutdated && isSignedIn && topic.isSystem && (
              <>
                {!userRanking?.publishDate ? (
                  <button onClick={handleVote}>Vote Now!</button>
                ) : (
                  <button onClick={handleVote}>Edit my Votes</button>
                )}
              </>
            )}
          </>
        )}

        {isVoting && topic.isSystem && (
          <div>
            <h3>Order Your Items (Drag to reorder)</h3>
            {items.map((item, index) => (
              <Item key={item.name + index} item={item} index={index} moveItem={moveItem} />
            ))}
            <button onClick={handlePublish}>Publish</button>
            <button onClick={handleCancel}>Cancel</button>
          </div>
        )}

        {!isSignedIn && topic.isSystem && (
          <div>
            <Link to="/sign-on">Sign-on to vote!</Link>
          </div>
        )}

        <Link to={topic.isSystem ? "/system-topics" : "/my-topics"}>Back to {topic.isSystem ? "System Topics" : "My Topics"}</Link>
      </div>
    </DndProvider>
  );
};

export default TopicDetails;